/**
 * Image Types
 * Types for image library and editor functionality
 */

// ==================== Image Context ====================

/**
 * Contexts where images can be used
 * Affects suggested aspect ratios and hints
 */
export type ImageContext =
  | 'hero' // Hero section image (4:3 landscape)
  | 'introImage' // Intro animation image (3:4 portrait)
  | 'gallery' // Gallery section (various)
  | 'middlePicture' // Middle section image
  | 'bottomPicture' // Bottom section image
  | 'hostPhoto' // Host profile photo (1:1 square)
  | 'keepsake' // Keepsake product image
  | 'profilePicture' // User profile picture (1:1 square)
  | 'stationeryFront' // Stationery invitation front image (5:7 portrait)
  | 'general' // No specific context

/**
 * Aspect ratio presets for cropping
 * Primary: 4:3, 3:4, 1:1 (matches phone cameras and templates)
 */
export type AspectRatio =
  | 'free' // No constraint
  | '1:1' // Square
  | '4:3' // Landscape (phone camera default)
  | '3:4' // Portrait (phone camera default)
  | '9:16' // Portrait tall (full-screen mobile)
  | '16:9' // Landscape wide (widescreen/cinematic)
  | '2:3' // Portrait (4×6 prints, simplified)
  | '3:2' // Landscape (6×4 prints, simplified)
  | '4:6' // Portrait (4×6 prints)
  | '6:4' // Landscape (6×4 prints)
  | '5:7' // Portrait (5×7 prints / stationery)
  | '7:5' // Landscape (7×5 prints)
  | '4:5' // Portrait (8×10 prints)
  | '5:4' // Landscape (10×8 prints)

/**
 * Mapping from context to suggested aspect ratio
 * Templates support: 4:3 (landscape), 3:4 (portrait), 1:1 (square)
 * These match phone camera native 4:3 ratio
 */
export const CONTEXT_ASPECT_RATIOS: Record<ImageContext, AspectRatio> = {
  hero: '4:3', // Landscape - matches phone camera
  introImage: '3:4', // Portrait - matches phone camera
  gallery: 'free',
  middlePicture: '4:3',
  bottomPicture: '4:3',
  hostPhoto: '1:1',
  keepsake: 'free', // Products will specify their own requirements
  profilePicture: '1:1',
  stationeryFront: '5:7', // Portrait - print invitation front
  general: 'free',
}

/**
 * Human-readable labels for image contexts
 */
export const CONTEXT_LABELS: Record<ImageContext, string> = {
  hero: 'Hero Image',
  introImage: 'Intro Image',
  gallery: 'Gallery',
  middlePicture: 'Middle Picture',
  bottomPicture: 'Bottom Picture',
  hostPhoto: 'Host Photo',
  keepsake: 'Keepsake',
  profilePicture: 'Profile Picture',
  stationeryFront: 'Stationery Front',
  general: 'Image',
}

/**
 * Recommended dimensions for each context
 * Based on 4:3 ratio for templates (matches phone cameras)
 */
export const CONTEXT_DIMENSIONS: Record<ImageContext, string> = {
  hero: '1600×1200', // 4:3 landscape
  introImage: '1200×1600', // 3:4 portrait
  gallery: 'Any size',
  middlePicture: '1200×900', // 4:3
  bottomPicture: '1200×900', // 4:3
  hostPhoto: '500×500', // 1:1
  keepsake: 'Varies by product',
  profilePicture: '400×400', // 1:1
  stationeryFront: '1500×2100', // 5:7 portrait (300 DPI for 5×7 print)
  general: 'Any size',
}

/**
 * Image selection limits for each context
 * min: minimum required, max: maximum allowed
 */
export const CONTEXT_IMAGE_LIMITS: Record<
  ImageContext,
  { min: number; max: number }
> = {
  hero: { min: 1, max: 1 },
  introImage: { min: 1, max: 1 },
  gallery: { min: 0, max: 50 },
  middlePicture: { min: 1, max: 1 },
  bottomPicture: { min: 1, max: 1 },
  hostPhoto: { min: 1, max: 1 },
  keepsake: { min: 1, max: 10 },
  profilePicture: { min: 1, max: 1 },
  stationeryFront: { min: 1, max: 1 },
  general: { min: 0, max: 100 },
}

// ==================== Image Type ====================

/**
 * Image type - distinguishes original uploads from edited versions
 * 'og' = original upload, 'edit' = edited version
 */
export type ImageType = 'og' | 'edit'

// ==================== Stored Image ====================

/**
 * Image stored in user's library
 * Matches backend LibraryImage type
 *
 * URL Format: Single base URL with Cloudflare transforms
 * Use getImageUrl(image.imageUrl, size) from lib/image-url.ts to get transformed URLs
 */
export interface StoredImage {
  id: string
  /** Original filename (e.g., "og-1703180245123-abc123") */
  filename: string
  /** Image type: 'og' = original upload, 'edit' = edited version */
  type: ImageType
  /** Base image URL - use getImageUrl() to generate size variants */
  imageUrl: string
  /** File size in bytes */
  size: number | null
  /** Total size in bytes (for guest uploads with multiple variants) */
  totalSizeBytes?: number
  /** Original image width in pixels */
  originalWidth: number | null
  /** Original image height in pixels */
  originalHeight: number | null
  /** Whether image meets print quality requirements */
  isPrintReady: boolean
  /** Associated invitation IDs - image can be shared across invitations */
  invitationIds: string[]
  /** When the image was uploaded (ISO string) */
  createdAt: string
  /** Whether this image is favorited by the user */
  isFavorite: boolean
  /** Who uploaded this image: 'user' | 'unauth-user' | '<guest name>' */
  uploadedBy?: string
  /** Guest rows: optional label from API (anon → null). */
  uploadedByLabel?: string | null
  /** Whether this image is hidden (for guest uploads moderation) */
  isHidden?: boolean
  /** When the image was hidden (ISO string) */
  hiddenAt?: string | null
  /** Reason for hiding the image */
  hiddenReason?: string | null

  // Frontend-only fields (not from API)
  /** Computed: Whether this is an edited version (type === 'edit') */
  isEdited?: boolean
  /** Frontend tracking: ID of the original image this was edited from */
  originalImageId?: string
  /** Frontend tracking: Edit metadata for undo/history */
  editData?: EditData
}

/**
 * Image status for filtering
 */
export type ImageStatus = 'all' | 'original' | 'edited' | 'favorites'

/**
 * Sort options for image library
 */
export type ImageSortOption = 'recent' | 'oldest' | 'name'

// ==================== Edit Data ====================

/**
 * Crop coordinates and dimensions (percentages 0-100)
 */
export interface CropData {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Complete edit metadata
 */
export interface EditData {
  cropData: CropData
  rotation: number // Degrees (0, 90, 180, 270)
  zoom: number // Zoom level (1 = 100%)
}

// ==================== Selection ====================

/**
 * What gets returned when user selects an image
 * Use getImageUrl(imageUrl, size) to generate display URLs
 */
export interface SelectedImage {
  id: string
  /** Base image URL - use getImageUrl() to get sized variants */
  imageUrl: string
  /** Whether this is an edited image */
  isEdited: boolean
}

// ==================== Upload ====================

/**
 * Upload progress state
 */
export interface UploadProgress {
  filename: string
  progress: number // 0-100
  status: 'uploading' | 'processing' | 'complete' | 'error'
  error?: string
}

// ==================== Helpers ====================

/** Convert aspect ratio string to numeric value */
export function getAspectRatioValue(ratio: AspectRatio): number | undefined {
  const ratios: Record<AspectRatio, number | undefined> = {
    free: undefined,
    '1:1': 1,
    '4:3': 4 / 3,
    '3:4': 3 / 4,
    '9:16': 9 / 16,
    '16:9': 16 / 9,
    '2:3': 2 / 3,
    '3:2': 3 / 2,
    '4:6': 4 / 6,
    '6:4': 6 / 4,
    '5:7': 5 / 7,
    '7:5': 7 / 5,
    '4:5': 4 / 5,
    '5:4': 5 / 4,
  }
  return ratios[ratio]
}

/** Normalize image from API - derives isEdited from type */
export function normalizeImage(image: StoredImage): StoredImage {
  return { ...image, isEdited: image.type === 'edit' }
}

/** Normalize array of images from API */
export function normalizeImages(images: StoredImage[]): StoredImage[] {
  return images.map(normalizeImage)
}
